import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit2, Trash2, Coins, Package, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function MasterCreditPlansPage() {
  const { api, user } = useAuthStore();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    credits: 10,
    price: 50,
    active: true
  });

  const userCredits = user?.credits || 0;

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/master/credit-plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pacotes');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        credits: plan.credits,
        price: plan.price,
        active: plan.active
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        credits: Math.min(10, userCredits),
        price: 50,
        active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.credits < 1 || formData.price < 0.01) {
      toast.error('Preencha todos os campos corretamente');
      return;
    }

    if (formData.credits > userCredits) {
      toast.error(`Você só pode criar pacotes de até ${userCredits} créditos (seu saldo atual)`);
      return;
    }

    try {
      if (editingPlan) {
        await api.put(`/master/credit-plans/${editingPlan.id}`, formData);
        toast.success('Pacote atualizado!');
      } else {
        await api.post('/master/credit-plans', formData);
        toast.success('Pacote criado!');
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar pacote');
    }
  };

  const handleDelete = async () => {
    if (!planToDelete) return;

    try {
      await api.delete(`/master/credit-plans/${planToDelete.id}`);
      toast.success('Pacote deletado!');
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      fetchPlans();
    } catch (error) {
      toast.error('Erro ao deletar pacote');
    }
  };

  const confirmDelete = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Package className="w-8 h-8 text-primary" />
            Meus Pacotes de Créditos
          </h1>
          <p className="text-muted-foreground">
            Crie pacotes para vender créditos aos seus revendedores e masters
          </p>
        </div>
        <Button onClick={() => openDialog()} disabled={userCredits < 1}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Pacote
        </Button>
      </div>

      {/* Aviso de saldo */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <Coins className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-blue-500">Seu saldo: {userCredits} créditos</p>
          <p className="text-muted-foreground">
            Quando alguém comprar um pacote seu, os créditos serão transferidos do seu saldo.
          </p>
        </div>
      </div>

      {userCredits < 1 && (
        <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-yellow-500">Sem créditos disponíveis</p>
            <p className="text-muted-foreground">
              Você precisa ter créditos para criar e vender pacotes. Compre créditos na loja.
            </p>
          </div>
        </div>
      )}

      {/* Lista de pacotes */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => (
          <Card key={plan.id} className="p-6 relative">
            <div className="absolute top-4 right-4">
              <Badge variant={plan.active ? 'default' : 'secondary'}>
                {plan.active ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.credits} créditos</p>
              </div>
            </div>

            <div className="text-3xl font-bold text-primary mb-4">
              R$ {plan.price.toFixed(2)}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDialog(plan)}
                className="flex-1"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Editar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => confirmDelete(plan)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}

        {plans.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum pacote criado ainda</p>
            <p className="text-sm">Crie pacotes para vender créditos aos seus usuários</p>
          </div>
        )}
      </div>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Editar Pacote' : 'Novo Pacote de Créditos'}
            </DialogTitle>
            <DialogDescription>
              {editingPlan 
                ? 'Atualize as informações do pacote'
                : 'Crie um pacote para vender aos seus revendedores'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nome do Pacote</Label>
              <Input
                placeholder="Ex: Pacote Básico"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Quantidade de Créditos</Label>
              <Input
                type="number"
                min={1}
                max={userCredits}
                value={formData.credits}
                onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 1 })}
              />
              <p className="text-xs text-muted-foreground">
                Máximo: {userCredits} créditos (seu saldo)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Preço (R$)</Label>
              <Input
                type="number"
                min={0.01}
                step={0.01}
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Pacote Ativo</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            <Button onClick={handleSubmit} className="w-full">
              {editingPlan ? 'Salvar Alterações' : 'Criar Pacote'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar Pacote?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar o pacote &quot;{planToDelete?.name}&quot;?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
